<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>クラウド向けパケットフィルタリング一覧</span>
        <manual-button path="/multicloud-packet-filtering-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="cloudPackets.search"
              v-model="cloudPackets.filter"
            />
          </v-col>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="cloudPackets.sortBy"
          primary-key="wnumberAct"
          :fields="cloudPackets.fields"
          :items="cloudPackets.items"
          :filterOption="cloudPackets.filter_option"
          :filter="cloudPackets.filter"
          :hover="true"
          :fixed="true"
          sticky-header="50vh"
          @click:row="showCloudPktRules"
          :td-class="cellClass"
        >
          <template #cell(menu)="data">
            {{ $filter.optionText(data.item.menu, cloudPktMenus) }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { CloudPktFilterEntity } from "@/apis/PacketFilteringApi";
import PacketFilteringReference from "@/modals/packetFiltering/PacketFilteringReference.vue";

const cloudPktMenus = [{ value: "MULTI_CLOUD_GW", text: "マルチクラウドGW" }];

/** パケットフィルタリング設定有無（検索窓選択肢用） */
export const statusItems = [
  { value: "FILTERING", text: "有り" },
  { value: "NO_FILTERING", text: "無し" },
];

export default defineComponent({
  name: "CloudPacketFilteringList",
  data() {
    return {
      isLoaded: false,
      statusItems: statusItems,
      cloudPktMenus: cloudPktMenus,
      cloudPackets: {
        sortBy: {
          key: "wnumberAct",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "wnumberAct",
            title: `VNコネクト番号(Act/Sby)`,
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: CloudPktFilterEntity) => {
              return !item.wnumberSby
                ? `${item.wnumberAct}\n-`
                : `${item.wnumberAct}\n${item.wnumberSby}`;
            },
          },
          {
            key: "vnConnectNameAct",
            width: "200px",
            title: "VNコネクト名(Act/Sby)",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: CloudPktFilterEntity) => {
              return !item.vnConnectNameSby
                ? `${item.vnConnectNameAct}\n-`
                : `${item.vnConnectNameAct}\n${item.vnConnectNameSby}`;
            },
          },
          {
            key: "enumber",
            width: "200px",
            title: "回線番号(E番号)",
            sortable: true,
          },
          {
            key: "menu",
            width: "300px",
            title: "メニュー",
          },
          {
            key: "dstVpnVnCode",
            width: "300px",
            title: "接続先VPN/VNコード",
            sortable: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: CloudPktFilterEntity) => {
              return item.vnName
                ? `${item.vnName}(${item.dstVpnVnCode})`
                : item.dstVpnVnCode;
            },
          },
          {
            key: "filteringSettingStatus",
            width: "340px",
            title: "パケットフィルタリング設定有無",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "filterringSettingStatus"),
          },
        ] as AppTableHeader<CloudPktFilterEntity>[],
        items: [] as CloudPktFilterEntity[],
        search: {
          wnumberAct: { type: "text", label: "VNコネクト番号" },
          vnConnectNameAct: { type: "text", label: "VNコネクト名" },
          enumber: { type: "text", label: "回線番号(E番号)" },
          dstVpnVnCode: { type: "text", label: "接続先VPN/VNコード" },
          filteringSettingStatus: {
            type: "select",
            label: "パケットフィルタリング設定有無",
            items: statusItems,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          wnumberAct: "like",
          vnConnectNameAct: "like",
          enumber: "like",
          dstVpnVnCode: "like",
          filteringSettingStatus: "equal",
        } as FilterOption,
      },
      vnConnectPacketFilterKeyId: "",
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      /** クラウド向けパケットフィルタリング一覧を取得 */
      const cloudData = await this.$api.packetFilter.getCloudVnConnects();
      this.cloudPackets.items = cloudData.packetFilterList.map(
        (e: CloudPktFilterEntity) =>
          ({
            ...e,
            menu: "MULTI_CLOUD_GW",
          }) as CloudPktFilterEntity
      );
      this.vnConnectPacketFilterKeyId = cloudData.vnConnectPacketFilterKeyId;
    },
    /** パケットフィルタルール表示 */
    async showCloudPktRules(item: CloudPktFilterEntity) {
      const data = (
        await this.$api.packetFilter.getCloudPktFilter(item.wnumberAct)
      ).packetFilterRuleList;
      const downloadUrl = this.$api.packetFilter.getCloudCsvURL(item.enumber);

      await this.$modal.show(PacketFilteringReference, {
        packetFilteringItem: data,
        clickRowCloudVnConItem: item,
        packetFilterKeyId: this.vnConnectPacketFilterKeyId,
        downloadUrl: downloadUrl,
      });
      await this.load();
    },
    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "wnumberAct" || field.key === "vnConnectNameAct") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
